import type { ReactNode } from 'react';

import { RouteError, RouteLoading } from './route-state';

/**
 * The part of a query a gate reads. Narrow on purpose, so an infinite query — whose
 * `data` is its pages — fits as well as a plain one.
 */
export interface GateableQuery<T> {
  readonly isPending: boolean;
  readonly data: T | undefined;
  readonly error: unknown;
  readonly refetch: () => unknown;
}

/**
 * The async gate every screen used to write out by hand: a spinner while the first
 * fetch is in flight, the retry panel when there is nothing to show, the screen when
 * there is.
 *
 * It gates on the data, not on `isError`: a background refetch that fails keeps the
 * screen it already has rather than replacing it with an error — the behaviour the
 * hand-written `if (!q.data) return <RouteError/>` had, kept on purpose.
 */
export function QueryGate<T>({
  query,
  children,
}: {
  query: GateableQuery<T>;
  children: (data: T) => ReactNode;
}) {
  if (query.isPending) return <RouteLoading />;
  if (query.data === undefined) return <RouteError error={query.error} onRetry={query.refetch} />;
  return children(query.data);
}
